import { z } from 'zod';
import { BillingPeriodSchema, CollectionMethodSchema, ContractStatusSchema, CurrencyCodeSchema, PricingModelSchema, } from '../common/enums.js';
import { MetadataSchema, PaginationQuerySchema } from '../common/types.js';
import { AppliesToSchema } from '../taxes/index.js';
export const LineItemTypeSchema = z.enum([
    'setup',
    'recurring',
    'usage_agent',
    'usage_feature',
]);
export const AddonTypeSchema = z.enum(['one_time', 'recurring', 'usage_based']);
export const DiscountTypeSchema = z.enum(['percentage', 'fixed_amount']);
export const StackingBehaviorSchema = z.enum([
    'additive',
    'exclusive',
    'multiplicative',
]);
export const CancellationTypeSchema = z.enum(['immediate', 'end_of_period']);
export const RefundTypeSchema = z.enum(['none', 'prorated', 'full']);
export const ProrationTypeSchema = z.enum([
    'none',
    'create_credit',
    'create_charge',
]);
export const ContractTierOverrideSchema = z.object({
    min_units: z.number().int().min(0),
    max_units: z.number().int().nullable(),
    unit_price: z.string(),
});
export const ContractLineItemOverrideSchema = z.object({
    line_item_type: LineItemTypeSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
    agent_id: z.string().optional(),
    signal_id: z.string().optional(),
    feature_id: z.string().optional(),
    unit_price: z.number().min(0),
    pricing_model: PricingModelSchema.default('flat'),
    included_units: z.number().min(0).default(0).optional(),
    tiers: z.array(ContractTierOverrideSchema).min(1).nullable().optional(),
    minimum_commitment: z.number().min(0).default(0).optional(),
    usage_cap: z.number().min(0).optional(),
    overage_rate: z.number().min(0).optional(),
    currency: CurrencyCodeSchema.optional(),
    billing_cycle: z.enum(['monthly', 'quarterly', 'yearly']).optional(),
});
export const ContractAddonSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    type: AddonTypeSchema.default('one_time'),
    amount: z.number().min(0),
    currency: CurrencyCodeSchema.optional(),
    quantity: z.number().min(1).default(1).optional(),
    billing_interval: BillingPeriodSchema.optional(),
    start_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    end_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const ContractAddonReferenceSchema = z.object({
    plan_id: z.string().describe('ID of the addon plan to attach'),
});
export const ContractTaxSchema = z.object({
    tax_id: z
        .string()
        .describe('ID of the tax from taxes table to apply to this contract'),
    priority: z.number().min(1).default(1).optional(),
});
export const ContractDiscountSchema = z.object({
    coupon_id: z.string().optional(),
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    type: DiscountTypeSchema.optional(),
    percentage_off: z.number().min(0).max(1).optional(),
    amount_off: z.number().min(0).optional(),
    currency: z.string().optional(),
    applies_to: AppliesToSchema.default('all'),
    valid_until: z.string().datetime().optional(),
    priority: z.number().min(1).default(1).optional(),
    stacking_behavior: StackingBehaviorSchema.default('additive').optional(),
    excludes_discount_ids: z.array(z.string()).optional(),
});
export const PlanSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    type: z.string(),
    status: z.string(),
});
export const CustomerSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    status: z.string(),
});
export const ContractSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    plan_id: z.string(),
    customer_id: z.string(),
    organization_id: z.string().nullable(),
    billing_entity_id: z.string().nullable(),
    contract_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: ContractStatusSchema,
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    end_date: z.string().nullable(),
    billing_interval: BillingPeriodSchema,
    currency: CurrencyCodeSchema,
    collection_method: CollectionMethodSchema,
    payment_terms: z.number().int(),
    trial_start: z.string().datetime().nullable(),
    trial_end: z.string().datetime().nullable(),
    signed_at: z.string().datetime().nullable(),
    activated_at: z.string().datetime().nullable(),
    canceled_at: z.string().datetime().nullable(),
    next_billing_date: z.string().datetime().nullable(),
    last_invoiced_at: z.string().datetime().nullable(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const ContractDiscountResponseSchema = z.object({
    id: z.string(),
    coupon_id: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    type: z.string(),
    percentage_off: z.number().nullable(),
    amount_off: z.number().nullable(),
    currency: z.string().nullable(),
    applies_to: z.string(),
    valid_until: z.string().nullable(),
    priority: z.number(),
    stacking_behavior: z.string(),
    status: z.string(),
    applied_at: z.string(),
    metadata: MetadataSchema.nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const ContractPricingTierResponseSchema = z.object({
    tier_number: z.number(),
    from_unit: z.number(),
    to_unit: z.number().nullable(),
    unit_price: z.number(),
    flat_fee: z.number().nullable(),
});
export const LineItemResponseSchema = z.object({
    id: z.string(),
    contract_id: z.string(),
    line_item_type: z.string(),
    type: z.enum(['plan', 'addon']),
    addon_id: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    agent_id: z.string().nullable(),
    signal_id: z.string().nullable(),
    feature_id: z.string().nullable(),
    unit_price: z.number(),
    pricing_model: z.string(),
    included_quantity: z.number(),
    included_units: z.number(),
    tiers: z.array(ContractPricingTierResponseSchema).nullable(),
    minimum_commitment: z.number(),
    usage_cap: z.number().nullable(),
    maximum_limit: z.number().nullable(),
    overage_rate: z.number().nullable(),
    currency: z.string(),
    billing_cycle: z.string().nullable(),
    billing_timing: z.string(),
    is_unlimited: z.boolean(),
    is_override: z.boolean(),
    original_price: z.number(),
    is_enabled: z.boolean(),
    metadata: z.object({}),
});
export const CreateContractSchema = z.object({
    plan_id: z.string().describe('ID of the plan to assign to this contract'),
    customer_id: z.string().describe('ID of the customer for this contract'),
    name: z.string().min(1).max(100).describe('Contract name'),
    description: z.string().max(500).optional(),
    start_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .describe('Contract start date (YYYY-MM-DD format)'),
    end_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .describe('Contract end date (YYYY-MM-DD format, optional for ongoing contracts)')
        .optional(),
    billing_interval: BillingPeriodSchema.optional(),
    currency: CurrencyCodeSchema.optional(),
    collection_method: CollectionMethodSchema.optional(),
    payment_terms: z
        .number()
        .int()
        .min(0)
        .max(365)
        .default(30)
        .describe('Payment terms in days (e.g., Net 30)')
        .optional(),
    line_item_overrides: z.array(ContractLineItemOverrideSchema).optional(),
    addons: z.array(ContractAddonSchema).optional(),
    addon_plan_ids: z.array(ContractAddonReferenceSchema).optional(),
    taxes: z.array(ContractTaxSchema).optional(),
    discounts: z.array(ContractDiscountSchema).optional(),
    trial_start: z.string().datetime().optional(),
    trial_end: z.string().datetime().optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateContractSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    end_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    billing_interval: BillingPeriodSchema.optional(),
    currency: CurrencyCodeSchema.optional(),
    collection_method: CollectionMethodSchema.optional(),
    payment_terms: z.number().int().min(0).max(365).optional(),
    metadata: MetadataSchema.optional(),
});
export const PatchContractSchema = z.object({
    status: z
        .enum([
        'draft',
        'active',
        'suspended',
        'terminated',
        'expired',
        'paused',
        'cancelled',
    ])
        .optional(),
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    end_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    billing_interval: z
        .enum(['monthly', 'quarterly', 'yearly', 'usage'])
        .optional(),
    currency: z.string().min(3).max(3).optional(),
    metadata: MetadataSchema.optional(),
});
export const ActivateContractSchema = z.object({
    activation_date: z.string().datetime().optional(),
    skip_validation: z.boolean().default(false).optional(),
});
export const SuspendContractSchema = z.object({
    suspend_reason: z.string().max(500).optional(),
    resume_date: z.string().datetime().optional(),
});
export const CancelContractSchema = z.object({
    cancellation_type: CancellationTypeSchema,
    refund_type: RefundTypeSchema.default('none').optional(),
    reason: z.string().max(500).optional(),
    cancellation_date: z.string().datetime().optional(),
});
export const ChangePlanSchema = z.object({
    new_plan_id: z.string(),
    change_date: z.string().datetime().optional(),
    proration_type: ProrationTypeSchema.default('create_credit'),
    preserve_overrides: z.boolean().default(false).optional(),
});
export const ApplyCouponToContractSchema = z.object({
    coupon_code: z.string().min(1).max(100),
    applies_to: AppliesToSchema.default('all').optional(),
    priority: z.number().min(1).default(1).optional(),
    stacking_behavior: StackingBehaviorSchema.default('additive').optional(),
    valid_until: z.string().datetime().optional(),
});
export const ValidateCouponForContractSchema = z.object({
    coupon_code: z.string().min(1).max(100),
});
export const CouponValidationResponseSchema = z.object({
    valid: z.boolean(),
    coupon: z
        .object({
        id: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        type: z.string(),
        percentage_rate: z.number().nullable(),
        amount_cents: z.number().nullable(),
        currency: z.string().nullable(),
        times_redeemed: z.number(),
        max_redemptions: z.number().nullable(),
    })
        .optional(),
    estimated_savings: z.number().optional(),
    conflicts: z.array(z.string()),
    errors: z.array(z.string()),
    message: z.string(),
});
export const ContractQuerySchema = PaginationQuerySchema.extend({
    search: z.string().optional(),
    status: ContractStatusSchema.optional(),
    customer_id: z.string().optional(),
    plan_id: z.string().optional(),
    billing_interval: BillingPeriodSchema.optional(),
    sort: z
        .enum(['name', 'start_date', 'created_at', 'status'])
        .default('created_at')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const ContractResponseSchema = ContractSchema.extend({
    plan: PlanSummarySchema,
    customer: CustomerSummarySchema,
});
export const PaginatedContractsResponseSchema = z.object({
    data: z.array(ContractResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const ContractLineItemResponseSchema = z.object({
    id: z.string(),
    type: z.enum(['charge', 'agent_action', 'feature']),
    source: z.enum(['plan', 'custom']),
    plan_charge_id: z.string().nullable().optional(),
    code: z.string().optional(),
    name: z.string(),
    charge_type: z.string().optional(),
    amount: z.number().optional(),
    billing_frequency: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    plan_agent_action_id: z.string().nullable().optional(),
    agent_action_id: z.string().optional(),
    action_type: z.string().optional(),
    plan_feature_id: z.string().nullable().optional(),
    feature_id: z.string().optional(),
    pricing_model: z.string().nullable().optional(),
    unit_price: z.number().nullable().optional(),
    package_size: z.number().nullable().optional(),
    package_price: z.number().nullable().optional(),
    included_units: z.number().nullable().optional(),
    minimum_commitment: z.number().nullable().optional(),
    overage_allowed: z.boolean().optional(),
    has_tiers: z.boolean().optional(),
    tiers: z.array(ContractPricingTierResponseSchema).optional(),
    metadata: MetadataSchema.nullable().optional(),
});
export const LineItemsResponseSchema = z.object({
    data: z.array(ContractLineItemResponseSchema),
});
export const ContractDiscountsResponseSchema = z.object({
    data: z.array(ContractDiscountResponseSchema),
});
export const AddContractAddonSchema = z.object({
    plan_id: z.string().describe('ID of the addon plan to add'),
    quantity: z.number().int().min(1).default(1).optional(),
    discount_percentage: z.number().min(0).max(100).optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateContractAddonSchema = z.object({
    quantity: z.number().int().min(1).optional(),
    discount_percentage: z.number().min(0).max(100).optional(),
    metadata: MetadataSchema.optional(),
});
export const ContractAddonResponseSchema = z.object({
    id: z.string(),
    addon_plan_id: z.string(),
    addon_name: z.string(),
    addon_code: z.string(),
    addon_description: z.string().nullable(),
    quantity: z.number(),
    discount_percentage: z.number().nullable(),
    metadata: MetadataSchema.nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});
export const ContractTaxResponseSchema = z.object({
    id: z.string(),
    contract_id: z.string(),
    tax_id: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    tax_type: z.string(),
    rate: z.number().nullable(),
    applies_to: z.string().nullable(),
    status: z.string().nullable(),
    region: z.string().nullable(),
    currency: z.string().nullable(),
    auto_apply: z.boolean().nullable(),
    metadata: MetadataSchema.nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});
export const ContractParamsSchema = z.object({
    id: z.string(),
});
export const ContractAddonParamsSchema = z.object({
    id: z.string(),
    addonId: z.string(),
});
export const ContractDiscountParamsSchema = z.object({
    id: z.string(),
    discountId: z.string(),
});
export const ContractTaxesResponseSchema = z.object({
    data: z.array(z.object({
        id: z.string(),
        tax_id: z.string(),
        name: z.string(),
        code: z.string(),
        description: z.string().nullable().optional(),
        tax_type: z.string(),
        rate: z.number(),
        percentage_value: z.number().nullable().optional(),
        fixed_amount_cents: z.number().nullable().optional(),
        applies_to: z.string().nullable().optional(),
        taxable_amount_type: z.string().nullable().optional(),
        compound: z.boolean().optional(),
        jurisdiction: z.string().nullable().optional(),
        jurisdiction_level: z.string().nullable().optional(),
        tax_authority: z.string().nullable().optional(),
        effective_from: z.string().nullable().optional(),
        effective_to: z.string().nullable().optional(),
        priority: z.number(),
        is_active: z.boolean(),
        autoApply: z.boolean(),
        auto_apply: z.boolean(),
        currency: z.string().nullable().optional(),
        status: z.string(),
        source: z.string().optional(),
        assigned_at: z.string().nullable().optional(),
        created_at: z.string().nullable().optional(),
        updated_at: z.string().nullable().optional(),
    })),
});
export const ApplyCouponResponseSchema = z.object({
    id: z.string(),
    contract_id: z.string(),
    coupon_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    type: z.string(),
    percentage_off: z.number().nullable(),
    amount_off: z.number().nullable(),
    currency: z.string().nullable(),
    applies_to: z.string(),
    valid_until: z.string().nullable(),
    priority: z.number(),
    stacking_behavior: z.string(),
    status: z.string(),
    applied_at: z.string(),
});
//# sourceMappingURL=index.js.map